import React, { useEffect, useRef, useState } from 'react';
import { Users, Zap, TrendingUp, Network, ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';

// Combined Stats and Testimonials Component
const ImpactAndTestimonials: React.FC = () => {
    // Stats section state
    const [isStatsVisible, setIsStatsVisible] = useState(false);
    const statsRef = useRef<HTMLDivElement>(null);

    // Testimonials section state
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsStatsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (statsRef.current) {
            observer.observe(statsRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const nextTestimonial = () => {
        setActiveIndex((current) => (current + 1) % testimonials.length);
    };

    const prevTestimonial = () => {
        setActiveIndex((current) => (current - 1 + testimonials.length) % testimonials.length);
    };

    const goToTestimonial = (index: number) => {
        setActiveIndex(index);
    };

    // Stats data
    const stats = [
        {
            icon: Users,
            value: "250,000",
            suffix: "+",
            label: "Network Members",
            description: "Growing community of clean energy users",
            color: "text-blue-500",
            bgColor: "bg-blue-50",
            iconBg: "bg-blue-100"
        },
        {
            icon: Zap,
            value: "45.8",
            suffix: " GWh",
            label: "Clean Energy Distributed",
            description: "Powering homes with renewable energy",
            color: "text-green-500",
            bgColor: "bg-green-50",
            iconBg: "bg-green-100"
        },
        {
            icon: TrendingUp,
            value: "32",
            suffix: "%",
            label: "Average Bill Reduction",
            description: "Saving money while saving the planet",
            color: "text-purple-500",
            bgColor: "bg-purple-50",
            iconBg: "bg-purple-100"
        },
        {
            icon: Network,
            value: "12,500",
            suffix: "+",
            label: "Digital Energy Assets",
            description: "P2P transactions across our smart grid",
            color: "text-teal-500",
            bgColor: "bg-teal-50",
            iconBg: "bg-teal-100"
        }
    ];

    // Testimonials data
    const testimonials = [
        {
            id: 1,
            name: "Sarah Johnson",
            role: "Homeowner with Solar Panels",
            image: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=600",
            quote: "Since joining Wenergie's network, my solar panels have become more than just an eco-friendly choice—they're a source of income. I love getting notifications when to expect peak generation times!",
            stars: 5
        },
        {
            id: 2,
            name: "Michael Chen",
            role: "Apartment Resident",
            image: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=600",
            quote: "Even without my own solar panels, I'm able to participate in the clean energy revolution. Wenergie's app alerts me when clean energy is abundant, so I can run my appliances guilt-free and save money.",
            stars: 5
        },
        {
            id: 3,
            name: "Emma Rodriguez",
            role: "Small Business Owner",
            image: "https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=600",
            quote: "Wenergie has transformed how our business uses electricity. We've reduced our carbon footprint and energy costs by scheduling operations during clean energy peaks. It's win-win!",
            stars: 4
        },
        {
            id: 4,
            name: "David Wilson",
            role: "Community Solar Farm Investor",
            image: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=600",
            quote: "The transparency of Wenergie's platform gives me confidence that my investment in community solar is making a real difference. I can literally see when my energy is being used by others.",
            stars: 5
        }
    ];

    return (
        <section className="relative py-20 overflow-hidden">
            {/* Background with gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-gray-50 -z-10" />

            {/* Decorative shapes */}
            <div className="absolute top-0 left-0 w-full h-64 overflow-hidden -z-5">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" />
                <div className="absolute top-40 left-2/3 w-96 h-96 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" />
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Main section header */}
                <div className="text-center max-w-3xl mx-auto mb-20">
                    <h2 className="text-sm uppercase tracking-widest text-blue-600 font-medium mb-4">Our Impact</h2>
                    <h3 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                        Powering A Sustainable Future
                    </h3>
                    <p className="text-lg text-gray-600">
                        Join a growing network of homes and businesses building the energy grid of tomorrow
                    </p>
                </div>

                {/* Stats Cards in horizontal scroll on mobile, grid on desktop */}
                <div ref={statsRef} className="relative mb-24">
                    <div className="flex flex-nowrap lg:grid lg:grid-cols-4 gap-6 overflow-x-auto pb-6 lg:overflow-visible scrollbar-hide">
                        {stats.map((stat, index) => (
                            <div
                                key={index}
                                className={`flex-shrink-0 w-80 lg:w-auto transition-all duration-700 transform ${isStatsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                                    }`}
                                style={{ transitionDelay: `${index * 150}ms` }}
                            >
                                <div className="bg-white rounded-2xl p-6 h-full shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100">
                                    {/* Icon */}
                                    <div className={`w-14 h-14 rounded-xl ${stat.iconBg} flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110`}>
                                        <stat.icon className={`h-7 w-7 ${stat.color}`} />
                                    </div>

                                    {/* Value */}
                                    <div className="mb-3">
                                        <span className={`text-4xl font-bold ${stat.color} transition-all duration-300`}>
                                            {isStatsVisible ? (
                                                <CountUp end={parseFloat(stat.value)} duration={2} />
                                            ) : '0'}
                                        </span>
                                        <span className={`text-2xl font-bold ${stat.color}`}>{stat.suffix}</span>
                                    </div>

                                    {/* Label */}
                                    <h4 className="text-gray-800 font-semibold mb-2">{stat.label}</h4>

                                    {/* Description */}
                                    <p className="text-gray-500 text-sm leading-relaxed">{stat.description}</p>

                                    {/* Progress Bar */}
                                    <div className="mt-4 h-1 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full ${stat.bgColor} transition-all duration-1000 ease-out`}
                                            style={{
                                                width: isStatsVisible ? '100%' : '0%',
                                                transitionDelay: `${index * 150 + 500}ms`
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Scroll indicator for mobile only */}
                    <div className="flex justify-center mt-4 lg:hidden">
                        <div className="flex space-x-2">
                            {[...Array(4)].map((_, idx) => (
                                <div key={idx} className={`w-2 h-2 rounded-full ${idx === 0 ? 'bg-blue-500' : 'bg-gray-300'}`} />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Divider with text */}
                <div className="relative flex items-center my-16">
                    <div className="flex-grow border-t border-gray-200"></div>
                    <span className="flex-shrink mx-4 text-gray-600">What Our Members Say</span>
                    <div className="flex-grow border-t border-gray-200"></div>
                </div>

                {/* Testimonials Section */}
                <div className="relative max-w-5xl mx-auto mb-16">
                    {/* Left Arrow */}
                    <button
                        onClick={prevTestimonial}
                        className="absolute left-0 lg:-left-8 top-1/2 -translate-y-1/2 z-20 bg-white rounded-full shadow-lg p-3 hover:bg-gray-50 transition-all duration-300 hover:shadow-xl"
                        aria-label="Previous testimonial"
                    >
                        <ChevronLeft className="h-6 w-6 text-gray-700" />
                    </button>

                    {/* Right Arrow */}
                    <button
                        onClick={nextTestimonial}
                        className="absolute right-0 lg:-right-8 top-1/2 -translate-y-1/2 z-20 bg-white rounded-full shadow-lg p-3 hover:bg-gray-50 transition-all duration-300 hover:shadow-xl"
                        aria-label="Next testimonial"
                    >
                        <ChevronRight className="h-6 w-6 text-gray-700" />
                    </button>

                    {/* Testimonial Cards with different transition */}
                    <div className="relative h-[400px] md:h-[300px] overflow-hidden">
                        {testimonials.map((testimonial, index) => (
                            <div
                                key={testimonial.id}
                                className={`absolute inset-0 transition-all duration-500 ${index === activeIndex
                                        ? 'opacity-100 scale-100 translate-y-0'
                                        : index < activeIndex
                                            ? 'opacity-0 scale-95 -translate-y-full'
                                            : 'opacity-0 scale-95 translate-y-full'
                                    }`}
                            >
                                <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10 h-full relative">
                                    {/* Quote Icon */}
                                    <div className="absolute top-6 right-6 opacity-10">
                                        <Quote className="h-20 w-20 text-gray-400" />
                                    </div>

                                    <div className="flex flex-col md:flex-row items-start gap-6 h-full">
                                        {/* Avatar */}
                                        <div className="flex-shrink-0">
                                            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden ring-4 ring-gray-100">
                                                <img
                                                    src={testimonial.image}
                                                    alt={testimonial.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 flex flex-col">
                                            {/* Stars */}
                                            <div className="flex items-center gap-1 mb-4">
                                                {[...Array(5)].map((_, i) => (
                                                    <Star
                                                        key={i}
                                                        className={`h-5 w-5 ${i < testimonial.stars
                                                                ? 'text-yellow-400 fill-current'
                                                                : 'text-gray-200 fill-current'
                                                            }`}
                                                    />
                                                ))}
                                            </div>

                                            {/* Quote */}
                                            <p className="text-gray-700 text-lg leading-relaxed mb-6 flex-1">
                                                {testimonial.quote}
                                            </p>

                                            {/* Author Info */}
                                            <div>
                                                <h4 className="text-lg font-semibold text-gray-900">
                                                    {testimonial.name}
                                                </h4>
                                                <p className="text-blue-600 font-medium">
                                                    {testimonial.role}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Navigation dots */}
                    <div className="flex justify-center space-x-3 mt-8">
                        {testimonials.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goToTestimonial(index)}
                                className={`transition-all duration-300 ${activeIndex === index
                                        ? 'w-8 h-2 bg-blue-600 rounded-full'
                                        : 'w-2 h-2 bg-gray-300 rounded-full hover:bg-gray-400'
                                    }`}
                                aria-label={`Go to testimonial ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>

                
            </div>
        </section>
    );
};

// Simple CountUp component
const CountUp: React.FC<{ end: number; duration: number }> = ({ end, duration }) => {
    const [count, setCount] = useState(0);
    const startTime = useRef<number>(0);
    const requestRef = useRef<number>();

    useEffect(() => {
        startTime.current = Date.now();

        const animate = () => {
            const now = Date.now();
            const progress = Math.min((now - startTime.current) / (duration * 1000), 1);

            setCount(Math.floor(end * progress));

            if (progress < 1) {
                requestRef.current = requestAnimationFrame(animate);
            }
        };

        requestRef.current = requestAnimationFrame(animate);

        return () => {
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
        };
    }, [end, duration]);

    return <>{count}</>;
};

export default ImpactAndTestimonials;